import { useState } from "react";

import dayjs from "dayjs";
import { Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import * as XLSX from "xlsx";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { useImportInventoryCount } from "~/hooks/costing";
import { useToast } from "~/hooks/use-toast";
import type { InventoryCountImportRow } from "~/services/costing";

import { parseLocaleNumber } from "./CosteoShared";

interface InventoryCountUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Month-end date the count belongs to, YYYY-MM-DD (e.g. 2026-07-31). */
  countDate: string;
  /** Display label for the month (e.g. "2026-07"). */
  monthLabel: string;
  plant?: string;
}

interface ParseResult {
  rows: InventoryCountImportRow[];
  warnings: string[];
  fatal?: string;
}

const findCol = (headers: string[], test: (h: string) => boolean) =>
  headers.findIndex((h) => test(String(h ?? "")));

/**
 * Parse the Softland "Informe de Stock Valorizado" export (Cód. Producto · Producto · Stock Total ·
 * Costo Unit. · Costo Total). Header matching is tolerant; the header row may be preceded by title rows.
 */
function parseWorkbook(data: string | ArrayBuffer): ParseResult {
  const wb = XLSX.read(data, { type: "binary" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    blankrows: false,
  });

  let headerIdx = -1;
  for (let i = 0; i < Math.min(grid.length, 15); i++) {
    const h = (grid[i] ?? []).map((c) => String(c ?? ""));
    const hasCode = h.some((x) => /c[oó]d/i.test(x) && !/costo/i.test(x));
    const hasQty = h.some((x) => /stock|cantidad/i.test(x));
    if (hasCode && hasQty) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) {
    return {
      rows: [],
      warnings: [],
      fatal:
        "No se reconocieron las columnas. Use el formato base (Cód. Producto, Producto, Stock Total, Costo Unit., Costo Total).",
    };
  }

  const headers = (grid[headerIdx] as unknown[]).map((c) => String(c ?? ""));
  const codeIdx = findCol(headers, (h) => /c[oó]d/i.test(h) && !/costo/i.test(h));
  const nameIdx = findCol(
    headers,
    (h) => /(producto|nombre|descrip)/i.test(h) && !/c[oó]d/i.test(h)
  );
  const qtyIdx = findCol(headers, (h) => /stock|cantidad/i.test(h));
  const unitIdx = findCol(headers, (h) => /costo\s*unit|unitario/i.test(h));
  const totalIdx = findCol(headers, (h) => /costo\s*total/i.test(h));
  const plantIdx = findCol(headers, (h) => /planta|bodega|almac/i.test(h));

  const rows: InventoryCountImportRow[] = [];
  const warnings: string[] = [];
  for (let i = headerIdx + 1; i < grid.length; i++) {
    const r = grid[i] as unknown[] | undefined;
    if (!r) continue;
    const code = String(r[codeIdx] ?? "").trim();
    if (!code || /^total/i.test(code)) continue; // skip blank / footer rows
    const qty = parseLocaleNumber(String(r[qtyIdx] ?? ""));
    const unit = unitIdx >= 0 ? parseLocaleNumber(String(r[unitIdx] ?? "")) : null;
    const totalCost = totalIdx >= 0 ? parseLocaleNumber(String(r[totalIdx] ?? "")) : null;

    if (qty === null && totalCost === null) {
      if (warnings.length < 6) warnings.push(`Fila ${i + 1} (${code}): sin cantidad ni costo total`);
      continue;
    }
    const real_qty = qty ?? 0;
    const unit_cost =
      unit ?? (totalCost !== null && real_qty !== 0 ? totalCost / real_qty : null);
    const total_cost = totalCost ?? (unit_cost !== null ? real_qty * unit_cost : null);
    const plant = plantIdx >= 0 ? String(r[plantIdx] ?? "").trim() : "";

    rows.push({
      code,
      name: nameIdx >= 0 ? String(r[nameIdx] ?? "").trim() || null : null,
      real_qty,
      unit_cost,
      total_cost,
      ...(plant ? { plant } : {}),
    });
  }
  return { rows, warnings };
}

export function InventoryCountUploadDialog({
  open,
  onOpenChange,
  countDate,
  monthLabel,
  plant = "CONS",
}: InventoryCountUploadDialogProps) {
  const { toast } = useToast();
  const importMutation = useImportInventoryCount();
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParseResult | null>(null);

  const reset = () => {
    setFileName(null);
    setParsed(null);
  };

  const downloadTemplate = () => {
    const example = [
      {
        "Cód. Producto": "01010013",
        Producto: "ACEITE REFINADO 100% GIRASOL RBD",
        Planta: "1",
        "Stock Total": 99167,
        "Costo Unit.": 1477.69,
        "Costo Total": 146538780,
      },
      {
        "Cód. Producto": "01070022",
        Producto: "CAJA VACIA BLANCA 500ML",
        Planta: "2",
        "Stock Total": 2869,
        "Costo Unit.": 363,
        "Costo Total": 1041447,
      },
      {
        "Cód. Producto": "01010013",
        Producto: "ACEITE REFINADO 100% GIRASOL RBD",
        Planta: "NC",
        "Stock Total": 500,
        "Costo Unit.": 1477.69,
        "Costo Total": 738845,
      },
    ];
    const ws = XLSX.utils.json_to_sheet(example);
    ws["!cols"] = [
      { wch: 16 },
      { wch: 48 },
      { wch: 10 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventario");
    XLSX.writeFile(wb, "formato_inventario_final.xlsx");
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setParsed(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        setParsed(parseWorkbook(e.target?.result as string));
      } catch (err) {
        setParsed({
          rows: [],
          warnings: [],
          fatal: err instanceof Error ? err.message : "No se pudo leer el archivo",
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleUpload = async () => {
    if (!parsed || parsed.rows.length === 0) return;
    try {
      const result = await importMutation.mutateAsync({
        countDate,
        plant,
        rows: parsed.rows,
      });
      toast({
        title: "Inventario cargado",
        description: `${result.imported} líneas cargadas${
          result.unmatched.length > 0
            ? ` · ${result.unmatched.length} sin registro en catálogo`
            : ""
        }`,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Error al cargar",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Cargar inventario — {monthLabel}
          </DialogTitle>
          <DialogDescription>
            Suba el archivo de Softland (Informe de Stock Valorizado) con las columnas{" "}
            <b>Cód. Producto · Producto · Stock Total · Costo Unit. · Costo Total</b>. Opcionalmente
            incluya una columna <b>Planta</b> (<b>1</b> = Planta 1, <b>2</b> = Planta 2, <b>NC</b> = No
            Conforme) para dividir el conteo por planta; si la omite, el conteo se carga consolidado. Se
            cargará como el conteo físico al{" "}
            {dayjs(countDate).format("DD/MM/YYYY")} y quedará disponible en la Variación Inventario para
            comparar con el sistema.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Button variant="outline" size="sm" className="h-9" onClick={downloadTemplate}>
            <Download className="mr-2 h-4 w-4" />
            Descargar formato base
          </Button>

          <Input
            type="file"
            accept=".xlsx,.xls"
            disabled={importMutation.isPending}
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
          />

          {parsed?.fatal && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {parsed.fatal}
            </div>
          )}

          {parsed && !parsed.fatal && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              {fileName && <span className="font-medium">{fileName}</span>}{" "}
              — <span className="text-green-700">{parsed.rows.length} filas válidas</span>
              {parsed.warnings.length > 0 && (
                <>
                  <span className="text-amber-700"> · {parsed.warnings.length} omitidas</span>
                  <ul className="mt-1 list-disc pl-5 text-xs text-amber-800">
                    {parsed.warnings.slice(0, 4).map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!parsed || parsed.rows.length === 0 || importMutation.isPending}
          >
            {importMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Cargar {parsed?.rows.length ? `(${parsed.rows.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
